import { RatingStat, Stats, StatsMonth, StatsRating } from '../types'
import { getStatsYear } from './date'
import { getDayCountInMonth } from './months'

export const getReadingGoalResult = ({ stats }: { stats: Stats | null }) => {
  const goal = stats?.reading_goal
  const numBooks = goal?.number_goal || 0
  const booksRead = goal?.number_achieved || 0
  const maxPercentage = 80
  const percentage =
    booksRead > numBooks
      ? maxPercentage
      : (booksRead / numBooks) * maxPercentage

  return { percentage, numBooks, booksRead }
}

export const getBestStreakResult = (stats: Stats | null) => {
  const bestStreak = stats?.streaks?.best_streak.count || 0
  const bestStreakText = bestStreak < 10 ? `0${bestStreak}` : bestStreak
  const bestStreakUnit = bestStreak === 1 ? 'day' : 'days'
  const text = bestStreakText === '00' ? '0' : bestStreakText

  return { bestStreak, bestStreakText: text, bestStreakUnit }
}

export const getStreakPercentage = (
  streak: number,
  month: StatsMonth,
  date: string
) => {
  const year = parseInt(getStatsYear(date))
  const dayCount = getDayCountInMonth(month, year)
  const percentage = (streak / dayCount) * 100
  return percentage > 100 ? 100 : percentage
}

export const getRatingsItems = (stats: Stats | null) => {
  const ratingsResults = stats?.ratings?.results || []

  const minRating = ratingsResults.reduce(
    (min: number, r: RatingStat) =>
      r.rating < min && r.count > 0 ? r.rating : min,
    5
  )

  const maxRating = ratingsResults.reduce(
    (max: number, r: RatingStat) =>
      r.rating > max && r.count > 0 ? r.rating : max,
    0
  )

  const ratings = ratingsResults.filter(
    (r) => r.rating >= minRating && r.rating <= maxRating
  )

  // create empty ratings columns when the range is less than 5
  if (ratings.length < 5) {
    Array.from({ length: 3 }).forEach((_, i) => {
      const index = i + 1
      const prev = minRating - index * 0.25

      if (prev >= 0.25) {
        ratings.unshift({ rating: prev as StatsRating, count: 0 })
      }
    })
    Array.from({ length: 3 }).forEach((_, i) => {
      const index = i + 1
      const next = maxRating + index * 0.25

      if (next <= 5) {
        ratings.push({ rating: next as StatsRating, count: 0 })
      }
    })
  }

  return ratings
}

export const getRatingResult = (stats: Stats | null) => {
  const averageRating = stats?.ratings?.average || 0
  const averageRatingText = averageRating.toFixed(1)
  const averageRatingUnit = averageRating === 1 ? 'star' : 'stars'
  const ratings = getRatingsItems(stats)

  return { averageRating, averageRatingUnit, averageRatingText, ratings }
}

export const getMonthlyBooksResult = (stats: Stats | null) => {
  const goal = stats?.reading_goal
  const booksRead =
    goal?.number_achieved || stats?.mom_books_read?.total_count || 0
  const booksReadText = booksRead < 10 ? `0${booksRead}` : booksRead
  const booksReadUnit = booksRead > 1 ? 'books' : 'book'

  return { booksRead, booksReadText, booksReadUnit }
}

export const isMinBooksRead = (stats: Stats | null) => {
  const goal = stats?.reading_goal
  const booksRead =
    goal?.number_achieved || stats?.mom_books_read?.total_count || 0

  return booksRead > 2
}
